import { ApolloQueryResult } from '@apollo/client'
import { CircularProgress, Tooltip, TableCell, TableRow } from '@mui/material'
import { ExternalLink } from 'lucide-react'
import {
  CampaignType,
  GetCampaignsQueryVariables,
  useGetScrapingStatusQuery,
  GetCampaignsQuery,
  Platform,
} from 'graphql/generated/graphql'
import Keyword from 'Keyword/Keyword'
import React from 'react'
import { Link } from 'react-router-dom'
import { CampaignTypeBadge } from '../CampaignTypeBadge/CampaignTypeBadge'

interface CampaignTableRowProps {
  campaignId: string
  hitsCount: number
  name: string
  keywords: string
  platforms: string
  type: CampaignType
  filterData: (
    variables?: GetCampaignsQueryVariables
  ) => Promise<ApolloQueryResult<GetCampaignsQuery>>
}

export default function CampaignTableRow(
  props: CampaignTableRowProps
): JSX.Element {
  const { campaignId, hitsCount, name, keywords, platforms, type, filterData } = props
  const [isHovered, setIsHovered] = React.useState(false)
  
  const { data: statusData, refetch: statusRefetch } = useGetScrapingStatusQuery({
    variables: {
      filter: {
        campaignId: Number(campaignId),
      },
    },
  })

  const statusTimeout = React.useRef<NodeJS.Timer | null>(null)
  const firstRender = React.useRef<boolean>(true)

  const [currentStatus, setCurrentStatus] = React.useState({
    instagram: false,
    youtube: false,
    reddit: false,
    tiktok: false,
    facebook: false,
  })

  // Determine the correct link based on campaign type
  const getCampaignLink = (): string => {
    if (type === CampaignType.Competitor) {
      return `/campaigns/${campaignId}/competitors`
    }
    return campaignId
  }

  const checkScrapingStatus = (): void => {
    statusTimeout.current = setInterval((): void => {
      statusRefetch().catch((e) => console.error(e))
    }, 5000)
  }

  const shouldClearInterval = (): boolean => {
    const finishedState = {
      instagram: false,
      youtube: false,
      reddit: false,
      tiktok: false,
      facebook: false,
    }
    // Instagram
    if (
      platforms.includes(Platform.Instagram) &&
      statusData?.scraping.instagram === false
    ) {
      finishedState.instagram = true
    } else if (!platforms.includes(Platform.Instagram)) {
      finishedState.instagram = true
    } else {
      finishedState.instagram = false
    }
    // Youtube
    if (
      platforms.includes(Platform.Youtube) &&
      statusData?.scraping.youtube === false
    ) {
      finishedState.youtube = true
    } else if (!platforms.includes(Platform.Youtube)) {
      finishedState.youtube = true
    } else {
      finishedState.youtube = false
    }
    // Reddit
    if (
      platforms.includes(Platform.Reddit) &&
      statusData?.scraping.reddit === false
    ) {
      finishedState.reddit = true
    } else if (!platforms.includes(Platform.Reddit)) {
      finishedState.reddit = true
    } else {
      finishedState.reddit = false
    }
    // TikTok
    if (
      platforms.includes(Platform.TikTok) &&
      statusData?.scraping.tiktok === false
    ) {
      finishedState.tiktok = true
    } else if (!platforms.includes(Platform.TikTok)) {
      finishedState.tiktok = true
    } else {
      finishedState.tiktok = false
    }
    // Facebook
    if (
      platforms.includes(Platform.Facebook) &&
      statusData?.scraping.facebook === false
    ) {
      finishedState.facebook = true
    } else if (!platforms.includes(Platform.Facebook)) {
      finishedState.facebook = true
    } else {
      finishedState.facebook = false
    }
    if (
      finishedState.instagram &&
      finishedState.youtube &&
      finishedState.reddit &&
      finishedState.tiktok &&
      finishedState.facebook
    ) {
      return true
    } else {
      return false
    }
  }

  React.useEffect(() => {
    if (firstRender.current) {
      checkScrapingStatus()
      setCurrentStatus({
        instagram: statusData?.scraping?.instagram ?? false,
        youtube: statusData?.scraping?.youtube ?? false,
        reddit: statusData?.scraping?.reddit ?? false,
        tiktok: statusData?.scraping?.tiktok ?? false,
        facebook: statusData?.scraping.facebook ?? false,
      })
      firstRender.current = false
    } else {
      if (platforms.includes(Platform.Instagram)) {
        if (currentStatus.instagram !== statusData?.scraping?.instagram) {
          filterData().catch((e) => console.log(e))
          setCurrentStatus((prev) => {
            return {
              ...prev,
              instagram: statusData?.scraping?.instagram ?? false,
            }
          })
        }
      } else {
        setCurrentStatus((prev) => {
          return {
            ...prev,
            instagram: false,
          }
        })
      }
      if (platforms.includes(Platform.Youtube)) {
        if (currentStatus.youtube !== statusData?.scraping?.youtube) {
          filterData().catch((e) => console.log(e))
          setCurrentStatus((prev) => {
            return {
              ...prev,
              youtube: statusData?.scraping?.youtube ?? false,
            }
          })
        }
      } else {
        setCurrentStatus((prev) => {
          return {
            ...prev,
            youtube: false,
          }
        })
      }
      if (platforms.includes(Platform.Reddit)) {
        if (currentStatus.reddit !== statusData?.scraping?.reddit) {
          filterData().catch((e) => console.log(e))
          setCurrentStatus((prev) => {
            return {
              ...prev,
              reddit: statusData?.scraping?.reddit ?? false,
            }
          })
        }
      } else {
        setCurrentStatus((prev) => {
          return {
            ...prev,
            reddit: false,
          }
        })
      }
      if (platforms.includes(Platform.TikTok)) {
        if (currentStatus.tiktok !== statusData?.scraping?.tiktok) {
          filterData().catch((e) => console.log(e))
          setCurrentStatus((prev) => {
            return {
              ...prev,
              tiktok: statusData?.scraping?.tiktok ?? false,
            }
          })
        }
      } else {
        setCurrentStatus((prev) => {
          return {
            ...prev,
            tiktok: false,
          }
        })
      }
      if (platforms.includes(Platform.Facebook)) {
        if (currentStatus.facebook !== statusData?.scraping?.facebook) {
          filterData().catch((e) => console.log(e))
          setCurrentStatus((prev) => {
            return {
              ...prev,
              facebook: statusData?.scraping?.facebook ?? false,
            }
          })
        }
      } else {
        setCurrentStatus((prev) => {
          return {
            ...prev,
            facebook: false,
          }
        })
      }
    }
    if (shouldClearInterval()) {
      clearInterval(statusTimeout.current ?? 0)
      setCurrentStatus({
        instagram: false,
        reddit: false,
        youtube: false,
        tiktok: false,
        facebook: false,
      })
    }
  }, [statusData])

  return (
    <TableRow
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s',
        backgroundColor: isHovered ? 'rgba(168, 85, 247, 0.08)' : 'transparent',
        borderBottom: '1px solid rgba(168, 85, 247, 0.1)',
        '&:last-child': {
          borderBottom: 'none',
        },
        '&:hover': {
          backgroundColor: 'rgba(168, 85, 247, 0.08)',
        },
      }}
    >
      {/* Campaign Name */}
      <TableCell
        sx={{
          border: 'none',
          maxWidth: '400px',
          overflow: 'hidden',
          padding: '16px 24px',
        }}
      >
        <Link
          to={getCampaignLink()}
          style={{
            color: 'white',
            fontWeight: 600,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            position: 'relative',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#a855f7'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'white'
          }}
        >
          {name}
          <ExternalLink
            style={{
              width: '16px',
              height: '16px',
              opacity: isHovered ? 1 : 0.5,
              transition: 'all 0.2s',
            }}
          />
        </Link>
      </TableCell>

      {/* Campaign Type */}
      <TableCell
        sx={{
          border: 'none',
          maxWidth: '400px',
          overflow: 'hidden',
          padding: '16px 24px',
        }}
      >
        <CampaignTypeBadge type={type} />
      </TableCell>

      {/* Keywords */}
      <TableCell
        sx={{
          border: 'none',
          maxWidth: '400px',
          overflow: 'hidden',
          padding: '16px 24px',
          fontSize: '0.75rem',
        }}
      >
        {(() => {
          const keywordArray = keywords.split(',').filter(el => el.length > 0)
          const showCount = keywordArray.length > 1
          
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {keywordArray.slice(0, 1).map((el, index) => (
                <Keyword text={el} key={index} />
              ))}
              {showCount && (
                <Tooltip title={keywordArray.join(', ')}>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      background: 'rgba(168, 85, 247, 0.2)',
                      borderRadius: '6px',
                      color: '#a855f7',
                      fontSize: '12px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    +{keywordArray.length - 1}
                  </div>
                </Tooltip>
              )}
            </div>
          )
        })()}
      </TableCell>

      {/* Hits Count */}
      <TableCell
        sx={{
          border: 'none',
          maxWidth: '400px',
          overflow: 'hidden',
          padding: '16px 24px',
        }}
      >
        {(platforms.includes(Platform.Youtube) && currentStatus.youtube) ||
        (platforms.includes(Platform.Reddit) && currentStatus.reddit) ||
        (platforms.includes(Platform.Instagram) && currentStatus.instagram) ||
        (platforms.includes(Platform.TikTok) && currentStatus.tiktok) ||
        (platforms.includes(Platform.Facebook) && currentStatus.facebook) ? (
          <Tooltip title="Fetching your data. Thanks for your patience!">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress
                size={20}
                sx={{
                  color: '#a855f7',
                }}
              />
            </div>
          </Tooltip>
        ) : (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '6px 12px',
              background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
              borderRadius: '8px',
              color: 'white',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            {hitsCount}
          </div>
        )}
      </TableCell>

      {/* Platforms */}
      <TableCell
        sx={{
          border: 'none',
          minWidth: '300px',
          maxWidth: '300px',
          overflow: 'hidden',
          padding: '16px 24px',
          fontSize: '0.75rem',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {platforms
            .split(',')
            .map((el, index) =>
              el.length > 0 ? (
                <Keyword key={index} platform={el} text={el} />
              ) : null
            )}
        </div>
      </TableCell>
    </TableRow>
  )
}
